<template>
  <div>
    <div class="max-w-50 md:max-w-xs mx-auto transform -translate-y-1/2">
      <segment-field
        v-model="_availabilityMode"
        :options="[
          { label: $t('form.find'), value: 'auto' },
          { label: $t('general.manual'), value: 'manual' }
        ]"
        class="shadow-sm"
      />
    </div>

    <div class="w-full max-w-full flex flex-col md:flex-row items-start gap-3">
      <div class="w-full md:w-64 md:py-2 px-3 flex flex-col justify-between">
        <template v-if="showWaitlist">
          <availability-waitlist v-model="date" :waitlist="waitlist" />
        </template>
        <template v-else>
          <div class="w-full flex justify-center md:justify-start">
            <availability-calendar v-model="date" :services="services" :groupMode="groupMode" class="md:origin-top-left" />
          </div>
        </template>

        <o-button v-if="hasWaitlist" flat size="sm" class="mt-4 mb-2" @click="showWaitlist = !showWaitlist">
          {{ showWaitlist ? $t('components.reservation.elements.availability.showAllDates') : $t('components.reservation.elements.availability.showWaitlistDates') }}
        </o-button>
      </div>
      <div class="w-full md:flex-1 min-w-0 max-h-xs p-2">
        <component
          :is="component"
          v-model:date="date"
          :startTime="startTime"
          :services="services"
          v-model:availabilitySlot="_availabilitySlot"
          v-model:loading="_loading"
          :groupMode="groupMode"
          v-model:override="_override"
          :hasConflicts="hasConflicts"
          :conflicts="conflicts"
          :move="move"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import AvailabilityWaitlist from '@/components/reservation/availability/date/Waitlist.vue'
import AvailabilityCalendar from '@/components/reservation/availability/date/Calendar.vue'
import AvailabilityAuto from '@/components/reservation/availability/auto/Controller.vue'
import AvailabilityManual from '@/components/reservation/availability/manual/Controller.vue'

import {
  SegmentField,
  ToggleField,
} from '@/components/fields'

import { AvailabilityRequestGroupMode, ConfirmationRequest } from '@/_api/models/booking'

const { $api, $dateFns } = useNuxtApp()

interface Props {
  availabilitySlot?: object | null
  services: any[]
  availabilityMode?: string
  groupMode?: any
  presets?: Record<string, any>
  waitlist?: object | null
  move?: object | null
  override?: boolean
  customer?: object | null
  validation?: object | null
  loading?: boolean
}

interface Emits {
  (e: 'update:availabilitySlot', value: any): void
  (e: 'update:availabilityMode', value: string): void
  (e: 'update:override', value: boolean): void
  (e: 'update:validation', value: any): void
  (e: 'update:loading', value: boolean): void
}

const props = withDefaults(defineProps<Props>(), {
  availabilitySlot: null,
  availabilityMode: 'auto',
  groupMode: () => AvailabilityRequestGroupMode.reservation,
  presets: () => ({}),
  waitlist: null,
  move: null,
  override: false,
  customer: null,
  validation: null,
  loading: false,
})

const emit = defineEmits<Emits>()

const { date: presetDate, startTime: presetStartTime } = props.presets
const date = ref($dateFns.startOfDay(presetDate || new Date()))
const startTime = ref(presetStartTime || null)
const showWaitlist = ref(!!props.waitlist)

const component = computed(() => {
  if (_availabilityMode.value === 'auto') {
    return AvailabilityAuto
  }

  return AvailabilityManual
})

const _availabilitySlot = computed({
  get () {
    return props.availabilitySlot
  },
  set (value) {
    emit('update:availabilitySlot', value)
  },
})

const _availabilityMode = computed({
  get () {
    return props.availabilityMode
  },
  set (value) {
    emit('update:availabilityMode', value)
  },
})

const _override = computed({
  get () {
    return props.override
  },
  set (value) {
    emit('update:override', value)
  },
})

const _validation = computed({
  get () {
    return props.validation
  },
  set (value) {
    emit('update:validation', value)
  },
})

const _loading = computed({
  get () {
    return props.loading
  },
  set (value) {
    emit('update:loading', value)
  },
})

watch(component, () => {
  _loading.value = false
})

const isGroupModeReservation = computed(() => {
  return props.groupMode === AvailabilityRequestGroupMode.reservation
})

const validationServices = computed(() => {
  const validation = props.validation as any
  const services = validation?.services ?? validation?.data?.services

  return Array.isArray(services) ? services : []
})

const conflicts = computed(() => {
  return validationServices.value
    .filter((service: any) => service?.conflicts?.length)
})

const hasConflicts = computed(() => {
  return conflicts.value.length > 0
})

const hasService = computed(() => !!props.services.length)
const hasWaitlist = computed(() => !!props.waitlist)

watch(_availabilitySlot, (availabilitySlot) => {
  if (availabilitySlot) {
    handleValidation()
  }
}, { deep: true })

watch(() => props.customer, (customer) => {
  if (customer) {
    handleValidation()
  }
}, { deep: true })

async function handleValidation () {
  try {
    const { availabilitySlot, customer, move } = props

    if (availabilitySlot && (customer || isGroupModeReservation.value)) {
      const request = ConfirmationRequest.fromSlot(availabilitySlot, { customer, move })

      if (!isGroupModeReservation.value && request.customer.isNewRecord) {
        request.customer = null
      }

      const validation = await $api.booking.confirmInfo(request)

      if (validation) {
        _validation.value = validation.data ?? validation
      }
    }
  } catch (e) {
    console.log(e)
  }
}
</script>
